import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";
import { setLoadingFilterName } from "../../../../store/state/FilterLoadingState";
import { useDispatch } from "react-redux";

interface BuyerSellerChartProps {
  data: any[];
  showLegend: boolean;
  theme?: "light" | "dark";
  type?: "buyer" | "seller";
  height?: number;
  onChartEvents?: any; // Accept onChartEvents as a prop
}

export const BuyerSellerChart: React.FC<BuyerSellerChartProps> = ({
  data,
  showLegend,
  theme = "light",
  type = "buyer",
  height = 400,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const dispatch = useDispatch();

  // Color schemes based on type
  const colorSchemes = {
    buyer: {
      gradient: ["#F1935C", "#D2691E"], // Original orange
      light: "#FFF4E6",
      main: "#F1935C",
      dark: "#D2691E",
    },
    seller: {
      gradient: ["#E8F4F5", "#34656D"], // Light teal to your specified color
      light: "#F0F8F9",
      main: "#34656D",
      dark: "#2A515A", // Darker version of #34656D
    },
  };

  const currentColors = colorSchemes[type];

  // Define option outside useEffect so it's accessible in JSX
  const sortedData = [...data]
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value)
    .reverse();

  const chartData = sortedData.map((item, index) => ({
    name: item.name,
    value: item.value,
    itemStyle: {
      color: {
        type: "linear" as const,
        x: 0,
        y: 0,
        x2: 1,
        y2: 0,
        colorStops: [
          {
            offset: 0,
            color: type === "seller" ? "#34656D" : "#F1935C",
          },
          {
            offset: 0.7,
            color: type === "seller" ? "#34656D" : "#F1935C",
          },
          {
            offset: 1,
            color: type === "seller" ? "#2A515A" : "#D2691E",
          },
        ],
      },
      borderRadius: [0, 8, 8, 0],
      shadowColor: "rgba(0, 0, 0, 0.1)",
      shadowBlur: 4,
      shadowOffsetX: 2,
      shadowOffsetY: 2,
    },
    emphasis: {
      itemStyle: {
        color: {
          type: "linear" as const,
          x: 0,
          y: 0,
          x2: 1,
          y2: 0,
          colorStops: [
            {
              offset: 0,
              color: currentColors.main,
            },
            {
              offset: 1,
              color: currentColors.dark,
            },
          ],
        },
        shadowColor: "rgba(0, 0, 0, 0.2)",
        shadowBlur: 8,
        shadowOffsetX: 4,
        shadowOffsetY: 4,
      },
    },
  }));

  const option: echarts.EChartsOption = useMemo(
    () => ({
      backgroundColor: theme === "dark" ? "#1F2937" : "transparent",
      tooltip: {
        trigger: "axis",
        position: function (point: any, _params: any, _dom: any, _rect: any, size: any) {
          const [contentWidth, contentHeight] = size.contentSize;
          const [viewWidth, viewHeight] = size.viewSize;
          const x = Math.min(Math.max(point[0] - contentWidth / 2, 10), viewWidth - contentWidth - 10);
          const spaceBelow = viewHeight - point[1];
          const y = spaceBelow >= contentHeight + 20 ? point[1] + 15 : point[1] - contentHeight - 15;
          return [x, Math.max(y, 5)];
        },
        axisPointer: {
          type: "shadow",
          shadowStyle: {
            color: "rgba(0, 0, 0, 0.1)",
          },
        },
        formatter: function (params: any) {
          if (!params || params.length === 0) return "";
          const param = params[0];
          const value = parseFloat(param.value.toString())
            .toFixed(2)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

          return `
         <div style="
font-family: 'Inter', sans-serif;
padding: 12px 16px;
background: linear-gradient(135deg, ${currentColors.main}15, ${currentColors.dark}15);
border-left: 4px solid ${currentColors.main};
border-radius: 8px;
box-shadow: 0 4px 12px rgba(0,0,0,0.15);
max-width: 250px;
word-break: break-all;
overflow-wrap: break-word;
white-space: normal;
">
<div style="
  font-weight: 700; 
  font-size: 14px; 
  color: #1F2937; 
  margin-bottom: 8px; 
  word-break: break-all;
  overflow-wrap: break-word;
  white-space: normal;
  line-height: 1.3;
">
  ${param.name}
</div>
<div style="display: flex; align-items: center; gap: 4px;">
  <span style="color: #6B7280; font-size: 12px;">Volume:</span>
  <span style="font-weight: 600; color: ${currentColors.dark}; font-size: 14px;">
    ${value} MWh
  </span>
</div>
</div>
        `;
        },
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderWidth: 0,
        borderRadius: 12,
        padding: 0,
        textStyle: {
          color: "#1F2937",
          fontFamily: "Inter, sans-serif",
        },
        extraCssText: `
        backdrop-filter: blur(10px);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.2);
      `,
      },
      grid: {
        left: 20,
        right: 110,
        top: 10,
        bottom: 10,
        containLabel: true,
      },
      xAxis: {
        type: "value",
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme === "dark" ? "#374151" : "#F3F4F6",
            type: "dashed",
            width: 1,
          },
        },
      },
      yAxis: {
        type: "category",
        data: chartData.map((item) => item.name),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontSize: 11,
          color: theme === "dark" ? "#D1D5DB" : "#4B5563",
          fontFamily: "Inter, sans-serif",
          fontWeight: 500,
          width: 100,
          overflow: "break",
          interval: 0,
          margin: 10,
          lineHeight: 14,
          rich: {
            name: {
              fontSize: 10,
              fontWeight: 500,
              color: theme === "dark" ? "#D1D5DB" : "#4B5563",
              lineHeight: 14,
              width: 170,
            },
          },
          formatter: function (value: string) {
            return value;
          },
        },
        inverse: false,
      },
      series: [
        {
          type: "bar",
          data: chartData,
          barWidth: 35,
          barCategoryGap: "25%",
          label: {
            show: true,
            position: "right",
            fontSize: 11,
            color: currentColors.dark,
            fontWeight: 700,
            fontFamily: "Inter, sans-serif",
            overflow: "none",
            formatter: function (params: any) {
              const value = parseFloat(params.value);
              const formattedValue = value
                .toFixed(1)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
              return `${formattedValue} MWh`;
            },
            distance: 6,
          },
          animationDelay: () => 100,
          animationDuration: 100,
          animationEasing: "cubicOut",
        },
      ],
      animation: true,
      animationDuration: 100,
      animationEasing: "cubicOut",
    }),
    [theme, currentColors.main, currentColors.dark, chartData]
  );

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;

    // Initialize chart
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    // Set option
    chartInstance.current.setOption(option, true);

    // Handle resize
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [
    currentColors.dark,
    currentColors.main,
    data,
    showLegend,
    theme,
    type,
    option,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  const onChartEvents = {
    finished: () => {
      requestAnimationFrame(() => dispatch(setLoadingFilterName("")));
    },
  };

  // Update the return statement to remove fixed height
  return (
    <div className="w-full relative">
      <ReactECharts
        option={option}
        onEvents={onChartEvents} // <-- Add this line
        style={{
          height: `${Math.max(
            data.filter((item) => item.value > 0).length * 55,
            120
          )}px`,
          width: "100%",
        }}
        theme={theme === "dark" ? "dark" : undefined}
      />
    </div>
  );
};

export default BuyerSellerChart;
